import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../lib/api';
import {
  Shield,
  Users,
  FileText,
  CheckCircle,
  Clock,
  BarChart3,
  LogOut,
  Loader2,
  Eye,
  ChevronRight,
  ArrowLeft,
  Crown,
  UserPlus,
  Trash2,
  Link,
  X,
  Plus,
  AlertCircle,
  UserCog,
  ChevronDown,
  FolderOpen,
  Upload,
  Download,
  File,
  HardDrive,
  Building2
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Client Management State
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientForm, setClientForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: ''
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  
  // User role management state
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(null);
  const [updatingRole, setUpdatingRole] = useState(null);
  
  // User creation state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [userForm, setUserForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    role: 'user'
  });
  
  // Password reset state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  // Data Room state
  const [showDataRoomModal, setShowDataRoomModal] = useState(false);
  const [dataRoomClient, setDataRoomClient] = useState(null);
  const [dataRoomFiles, setDataRoomFiles] = useState([]);
  const [dataRoomLoading, setDataRoomLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileDescription, setFileDescription] = useState('');

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, assessmentsRes, clientsRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers(),
        adminAPI.getAllAssessments(),
        adminAPI.getClients()
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setAssessments(assessmentsRes.data);
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setActionError('');
    try {
      await adminAPI.createClient(clientForm);
      setShowCreateClientModal(false);
      setClientForm({ email: '', firstName: '', lastName: '', password: '' });
      // Refresh clients list
      const clientsRes = await adminAPI.getClients();
      setClients(clientsRes.data || []);
    } catch (error) {
      setActionError(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignAssessment = async (assessmentId) => {
    if (!selectedClient) return;
    setActionLoading(true);
    setActionError('');
    try {
      await adminAPI.assignAssessment({
        clientId: selectedClient.id,
        assessmentId: assessmentId
      });
      setShowAssignModal(false);
      setSelectedClient(null);
      // Refresh clients list
      const clientsRes = await adminAPI.getClients();
      setClients(clientsRes.data || []);
    } catch (error) {
      setActionError(error.response?.data?.detail || 'Failed to assign assessment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnassignAssessment = async (clientId, assessmentId) => {
    setActionLoading(true);
    try {
      await adminAPI.unassignAssessment(clientId, assessmentId);
      // Refresh clients list
      const clientsRes = await adminAPI.getClients();
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Failed to unassign assessment:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    setActionLoading(true);
    try {
      await adminAPI.deleteClient(clientId);
      // Refresh clients list
      const clientsRes = await adminAPI.getClients();
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Failed to delete client:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Get assessments not yet assigned to a client
  const getAvailableAssessments = (client) => {
    const assignedIds = client.assignedAssessments || [];
    return assessments.filter(a => !assignedIds.includes(a.id));
  };

  // User creation
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setActionError('');
    try {
      await adminAPI.createUser(userForm);
      // Refresh users list
      const usersRes = await adminAPI.getUsers();
      setUsers(usersRes.data);
      setShowCreateUserModal(false);
      setUserForm({ email: '', firstName: '', lastName: '', password: '', role: 'user' });
    } catch (error) {
      console.error('Failed to create user:', error);
      setActionError(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  // Password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setActionError('Password must be at least 8 characters');
      return;
    }
    setActionLoading(true);
    setActionError('');
    try {
      await adminAPI.resetUserPassword(resetPasswordUser.id, newPassword);
      setShowResetPasswordModal(false);
      setResetPasswordUser(null);
      setNewPassword('');
      alert(`Password reset successfully for ${resetPasswordUser.email}`);
    } catch (error) {
      console.error('Failed to reset password:', error);
      setActionError(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  // User role management
  const handleUpdateUserRole = async (userId, newRole) => {
    setUpdatingRole(userId);
    try {
      await adminAPI.updateUserRole(userId, newRole);
      // Refresh users list
      const usersRes = await adminAPI.getUsers();
      setUsers(usersRes.data);
      setRoleDropdownOpen(null);
    } catch (error) {
      console.error('Failed to update user role:', error);
      alert(error.response?.data?.detail || 'Failed to update user role');
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This will also delete all their assessments.`)) return;
    
    setUpdatingRole(userId);
    try {
      await adminAPI.deleteUser(userId);
      // Refresh users list
      const usersRes = await adminAPI.getUsers();
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setUpdatingRole(null);
    }
  };

  const getRoleBadgeStyle = (role, isAdmin) => {
    if (isAdmin || role === 'admin') return 'bg-amber-500/20 text-amber-400';
    if (role === 'client') return 'bg-purple-500/20 text-purple-400';
    return 'bg-slate-700 text-slate-400';
  };

  const getRoleLabel = (u) => {
    if (u.isAdmin || u.role === 'admin') return 'Admin';
    if (u.role === 'client') return 'Client';
    return 'User';
  };

  // Data Room functions
  const openDataRoom = async (client) => {
    setDataRoomClient(client);
    setShowDataRoomModal(true);
    setDataRoomLoading(true);
    try {
      const res = await adminAPI.getDataRoomFiles(client.id);
      setDataRoomFiles(res.data || []);
    } catch (error) {
      console.error('Failed to fetch data room files:', error);
    } finally {
      setDataRoomLoading(false);
    }
  };

  const handleDataRoomUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !dataRoomClient) return;
    
    setUploadingFile(true);
    try {
      await adminAPI.uploadDataRoomFile(dataRoomClient.id, file, fileDescription);
      // Refresh files
      const res = await adminAPI.getDataRoomFiles(dataRoomClient.id);
      setDataRoomFiles(res.data || []);
      setFileDescription('');
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDataRoomDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    if (!dataRoomClient) return;
    
    try {
      await adminAPI.deleteDataRoomFile(dataRoomClient.id, fileId);
      setDataRoomFiles(dataRoomFiles.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-xs text-amber-400">Cybersecurity Administration</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {user?.isSuperAdmin && (
                <button
                  onClick={() => navigate('/admin/tenants')}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
                  data-testid="super-admin-btn"
                >
                  <Building2 className="w-4 h-4" />
                  Multi-Tenant
                </button>
              )}
              <button
                onClick={() => navigate('/admin/question-cms')}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
                data-testid="question-cms-btn"
              >
                <FileText className="w-4 h-4" />
                Question CMS
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 px-4 py-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                User View
              </button>
              <span className="text-amber-400 font-medium">
                {user?.firstName} {user?.lastName}
              </span>
              <button
                data-testid="admin-logout-btn"
                onClick={logout}
                className="flex items-center gap-2 px-4 py-2 text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl p-6 border border-amber-500/30">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-amber-200 text-sm">Total Users</p>
                <p className="text-2xl font-bold text-white">{stats?.totalUsers || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Assessments</p>
                <p className="text-2xl font-bold text-white">{stats?.totalAssessments || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Completed</p>
                <p className="text-2xl font-bold text-white">{stats?.completed || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">In Progress</p>
                <p className="text-2xl font-bold text-white">{stats?.inProgress || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Avg. Score</p>
                <p className="text-2xl font-bold text-white">{stats?.averageScore || 0}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['overview', 'users', 'assessments', 'clients'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2 rounded-lg font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-amber-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Assessment Type Distribution */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Assessment Types</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-blue-400">CMMC</span>
                    <span className="text-white">{stats?.cmmcCount || 0}</span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${stats?.totalAssessments ? (stats.cmmcCount / stats.totalAssessments * 100) : 0}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-green-400">HIPAA</span>
                    <span className="text-white">{stats?.hipaaCount || 0}</span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${stats?.totalAssessments ? (stats.hipaaCount / stats.totalAssessments * 100) : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {stats?.recentAssessments?.slice(0, 5).map((assessment, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Shield className={`w-5 h-5 ${assessment.assessmentType === 'CMMC' ? 'text-blue-400' : 'text-green-400'}`} />
                      <div>
                        <div className="text-white text-sm font-medium">{assessment.name}</div>
                        <div className="text-slate-400 text-xs">{assessment.userEmail || 'Unknown'}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-medium">{assessment.score}%</div>
                      <div className={`text-xs ${assessment.status === 'Completed' ? 'text-green-400' : 'text-yellow-400'}`}>
                        {assessment.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">User Management</h3>
                <p className="text-slate-400 text-sm">Manage user roles, permissions, and passwords</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded">Admin</span>
                  <span className="px-2 py-1 bg-slate-700 text-slate-400 rounded">User</span>
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded">Client</span>
                </div>
                <button
                  onClick={() => setShowCreateUserModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors"
                  data-testid="create-user-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  Create User
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800">
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Assessments</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {users.map((u) => {
                    const userAssessments = assessments.filter(a => a.userId === u.id);
                    const isCurrentUser = u.id === user?.id;
                    const currentRole = getRoleLabel(u);
                    
                    return (
                      <tr key={u.id} className={`hover:bg-slate-700/50 ${isCurrentUser ? 'bg-amber-500/5' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getRoleBadgeStyle(u.role, u.isAdmin)}`}>
                              {u.isAdmin || u.role === 'admin' ? (
                                <Crown className="w-5 h-5 text-amber-400" />
                              ) : u.role === 'client' ? (
                                <Users className="w-5 h-5 text-purple-400" />
                              ) : (
                                <span className="text-white font-medium">
                                  {(u.firstName?.[0] || '') + (u.lastName?.[0] || u.email?.[0] || '?')}
                                </span>
                              )}
                            </div>
                            <div>
                              <div className="text-white font-medium flex items-center gap-2">
                                {u.firstName} {u.lastName}
                                {isCurrentUser && (
                                  <span className="text-xs text-amber-400">(You)</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-slate-300">{u.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {/* Role Dropdown */}
                          <div className="relative inline-block">
                            <button
                              onClick={() => !isCurrentUser && setRoleDropdownOpen(roleDropdownOpen === u.id ? null : u.id)}
                              disabled={isCurrentUser || updatingRole === u.id}
                              className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-2 transition-colors ${
                                getRoleBadgeStyle(u.role, u.isAdmin)
                              } ${!isCurrentUser ? 'hover:opacity-80 cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
                            >
                              {updatingRole === u.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  {currentRole}
                                  {!isCurrentUser && <ChevronDown className="w-3 h-3" />}
                                </>
                              )}
                            </button>
                            
                            {/* Dropdown Menu */}
                            {roleDropdownOpen === u.id && (
                              <div className="absolute z-10 mt-1 w-32 bg-slate-700 border border-slate-600 rounded-lg shadow-xl overflow-hidden">
                                {['admin', 'user', 'client'].map((role) => (
                                  <button
                                    key={role}
                                    onClick={() => handleUpdateUserRole(u.id, role)}
                                    className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-600 transition-colors ${
                                      (u.role === role || (role === 'admin' && u.isAdmin)) ? 'bg-slate-600' : ''
                                    }`}
                                  >
                                    <span className={`inline-flex items-center gap-2 ${
                                      role === 'admin' ? 'text-amber-400' :
                                      role === 'client' ? 'text-purple-400' : 'text-slate-300'
                                    }`}>
                                      {role === 'admin' && <Crown className="w-3 h-3" />}
                                      {role === 'user' && <UserCog className="w-3 h-3" />}
                                      {role === 'client' && <Users className="w-3 h-3" />}
                                      {role.charAt(0).toUpperCase() + role.slice(1)}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-white">{userAssessments.length}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-slate-400 text-sm">
                          {u.createdAt ? formatDate(u.createdAt) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-1">
                            {!isCurrentUser && (
                              <>
                                <button
                                  onClick={() => {
                                    setResetPasswordUser(u);
                                    setShowResetPasswordModal(true);
                                  }}
                                  disabled={updatingRole === u.id}
                                  className="p-2 text-slate-400 hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-colors disabled:opacity-50"
                                  title="Reset password"
                                  data-testid={`reset-password-${u.id}`}
                                >
                                  <UserCog className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(u.id, `${u.firstName} ${u.lastName}`.trim() || u.email)}
                                  disabled={updatingRole === u.id}
                                  className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                                  title="Delete user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Assessments Tab */}
        {activeTab === 'assessments' && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800">
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Assessment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Progress</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Last Modified</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {assessments.map((assessment) => (
                    <tr key={assessment.id} className="hover:bg-slate-700/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-white font-medium">{assessment.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-slate-300">{assessment.userName}</div>
                        <div className="text-slate-500 text-xs">{assessment.userEmail}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          assessment.assessmentType === 'CMMC'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {assessment.assessmentType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          assessment.status === 'Completed'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {assessment.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-slate-300">
                        {assessment.answeredQuestions}/{assessment.totalQuestions}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`font-medium ${
                          assessment.score >= 70 ? 'text-green-400' :
                          assessment.score >= 40 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {assessment.score}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-slate-400 text-sm">
                        {formatDate(assessment.lastModified)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => navigate(`/report/${assessment.id}`)}
                          className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                          title="View Report"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="space-y-6">
            {/* Header with Create Button */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-white">Client Management</h2>
                <p className="text-slate-400 text-sm">Manage read-only client access to assessments</p>
              </div>
              <button
                onClick={() => setShowCreateClientModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                Add Client
              </button>
            </div>

            {/* Clients List */}
            {clients.length === 0 ? (
              <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
                <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Clients Yet</h3>
                <p className="text-slate-400 mb-4">Create client accounts to give read-only access to assessments.</p>
                <button
                  onClick={() => setShowCreateClientModal(true)}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
                >
                  Add Your First Client
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {clients.map((client) => (
                  <div key={client.id} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                          <Users className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{client.firstName} {client.lastName}</h3>
                          <p className="text-slate-400 text-sm">{client.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openDataRoom(client)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
                          title="Open Data Room"
                        >
                          <FolderOpen className="w-3 h-3" />
                          Data Room
                        </button>
                        <button
                          onClick={() => {
                            setSelectedClient(client);
                            setShowAssignModal(true);
                          }}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                          title="Assign Assessment"
                        >
                          <Link className="w-3 h-3" />
                          Assign
                        </button>
                        <button
                          onClick={() => handleDeleteClient(client.id)}
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Delete Client"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Assigned Assessments */}
                    <div>
                      <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Assigned Assessments</p>
                      {client.assignedAssessmentDetails?.length > 0 ? (
                        <div className="space-y-2">
                          {client.assignedAssessmentDetails.map((assessment) => (
                            <div key={assessment.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <Shield className={`w-4 h-4 ${
                                  assessment.assessmentType === 'CMMC' ? 'text-blue-400' :
                                  assessment.assessmentType === 'NIST_CSF' ? 'text-purple-400' : 'text-green-400'
                                }`} />
                                <span className="text-white text-sm">{assessment.name}</span>
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  assessment.assessmentType === 'CMMC' ? 'bg-blue-500/20 text-blue-400' :
                                  assessment.assessmentType === 'NIST_CSF' ? 'bg-purple-500/20 text-purple-400' : 'bg-green-500/20 text-green-400'
                                }`}>
                                  {assessment.assessmentType === 'NIST_CSF' ? 'NIST CSF' : assessment.assessmentType}
                                </span>
                              </div>
                              <button
                                onClick={() => handleUnassignAssessment(client.id, assessment.id)}
                                className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                                title="Remove assignment"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm italic">No assessments assigned</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create Client Modal */}
        {showCreateClientModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-md border border-slate-700 shadow-xl">
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Create New Client</h3>
                  <button
                    onClick={() => {
                      setShowCreateClientModal(false);
                      setClientForm({ email: '', firstName: '', lastName: '', password: '' });
                      setActionError('');
                    }}
                    className="p-1 text-slate-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-slate-400 text-sm mt-1">
                  Client accounts have read-only access to assigned assessments.
                </p>
              </div>
              
              <form onSubmit={handleCreateClient} className="p-6 space-y-4">
                {actionError && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {actionError}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">First Name</label>
                    <input
                      type="text"
                      value={clientForm.firstName}
                      onChange={(e) => setClientForm({ ...clientForm, firstName: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Last Name</label>
                    <input
                      type="text"
                      value={clientForm.lastName}
                      onChange={(e) => setClientForm({ ...clientForm, lastName: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Email</label>
                  <input
                    type="email"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Password</label>
                  <input
                    type="password"
                    value={clientForm.password}
                    onChange={(e) => setClientForm({ ...clientForm, password: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                    minLength={6}
                  />
                  <p className="text-slate-500 text-xs mt-1">Minimum 6 characters</p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateClientModal(false);
                      setClientForm({ email: '', firstName: '', lastName: '', password: '' });
                      setActionError('');
                    }}
                    className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                    Create Client
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Assign Assessment Modal */}
        {showAssignModal && selectedClient && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-lg border border-slate-700 shadow-xl">
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Assign Assessment</h3>
                  <button
                    onClick={() => {
                      setShowAssignModal(false);
                      setSelectedClient(null);
                      setActionError('');
                    }}
                    className="p-1 text-slate-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-slate-400 text-sm mt-1">
                  Assign an assessment to <span className="text-white font-medium">{selectedClient.firstName} {selectedClient.lastName}</span>
                </p>
              </div>
              
              <div className="p-6">
                {actionError && (
                  <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {actionError}
                  </div>
                )}
                
                {getAvailableAssessments(selectedClient).length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                    <p className="text-slate-400">No assessments available to assign.</p>
                    <p className="text-slate-500 text-sm">All assessments are already assigned to this client.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {getAvailableAssessments(selectedClient).map((assessment) => (
                      <button
                        key={assessment.id}
                        onClick={() => handleAssignAssessment(assessment.id)}
                        disabled={actionLoading}
                        className="w-full flex items-center justify-between p-4 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors text-left disabled:opacity-50"
                      >
                        <div className="flex items-center gap-3">
                          <Shield className={`w-5 h-5 ${
                            assessment.assessmentType === 'CMMC' ? 'text-blue-400' :
                            assessment.assessmentType === 'NIST_CSF' ? 'text-purple-400' : 'text-green-400'
                          }`} />
                          <div>
                            <div className="text-white font-medium">{assessment.name}</div>
                            <div className="text-slate-400 text-sm">
                              {assessment.assessmentType === 'NIST_CSF' ? 'NIST CSF' : assessment.assessmentType} • {assessment.status}
                            </div>
                          </div>
                        </div>
                        <Plus className="w-5 h-5 text-slate-400" />
                      </button>
                    ))}
                  </div>
                )}
                
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedClient(null);
                    setActionError('');
                  }}
                  className="w-full mt-4 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Data Room Modal */}
        {showDataRoomModal && dataRoomClient && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-slate-800 rounded-xl w-full max-w-2xl border border-slate-700 shadow-xl my-8">
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                      <HardDrive className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">Data Room</h3>
                      <p className="text-slate-400 text-sm">
                        {dataRoomClient.firstName} {dataRoomClient.lastName}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowDataRoomModal(false);
                      setDataRoomClient(null);
                      setDataRoomFiles([]);
                      setFileDescription('');
                    }}
                    className="p-1 text-slate-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {/* Upload Section */}
                <div className="mb-6 p-4 bg-slate-700/50 rounded-lg border border-dashed border-slate-600">
                  <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
                    <div className="flex-1">
                      <label className="block text-slate-300 text-sm mb-2">File Description (optional)</label>
                      <input
                        type="text"
                        value={fileDescription}
                        onChange={(e) => setFileDescription(e.target.value)}
                        placeholder="Brief description of the file..."
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                    </div>
                    <label className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg cursor-pointer transition-colors text-sm whitespace-nowrap">
                      {uploadingFile ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      Upload File
                      <input
                        type="file"
                        onChange={handleDataRoomUpload}
                        className="hidden"
                        disabled={uploadingFile}
                      />
                    </label>
                  </div>
                </div>

                {/* Files List */}
                {dataRoomLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
                  </div>
                ) : dataRoomFiles.length === 0 ? (
                  <div className="text-center py-12">
                    <FolderOpen className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No files uploaded yet</p>
                    <p className="text-slate-500 text-sm">Upload files to share with this client</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-slate-400 text-sm">{dataRoomFiles.length} file(s)</p>
                    {dataRoomFiles.map((file) => (
                      <div key={file.id} className="flex items-center gap-4 p-4 bg-slate-700/50 rounded-lg">
                        <div className="w-10 h-10 bg-slate-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <File className="w-5 h-5 text-slate-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{file.fileName}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-400">
                            <span>{formatFileSize(file.fileSize)}</span>
                            <span>•</span>
                            <span>{formatDate(file.uploadedAt)}</span>
                            {file.description && (
                              <>
                                <span>•</span>
                                <span className="text-slate-500 truncate">{file.description}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={adminAPI.getDataRoomDownloadUrl(dataRoomClient.id, file.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-slate-400 hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => handleDataRoomDelete(file.id)}
                            className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Create User Modal */}
        {showCreateUserModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-md border border-slate-700 shadow-xl">
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Create New User</h3>
                  <button
                    onClick={() => {
                      setShowCreateUserModal(false);
                      setUserForm({ email: '', firstName: '', lastName: '', password: '', role: 'user' });
                      setActionError('');
                    }}
                    className="p-1 text-slate-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-slate-400 text-sm mt-1">
                  Create a new user account with specified role and permissions.
                </p>
              </div>
              
              <form onSubmit={handleCreateUser} className="p-6 space-y-4">
                {actionError && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {actionError}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">First Name</label>
                    <input
                      type="text"
                      value={userForm.firstName}
                      onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                      required
                      data-testid="user-firstname"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Last Name</label>
                    <input
                      type="text"
                      value={userForm.lastName}
                      onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                      required
                      data-testid="user-lastname"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Email</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                    required
                    data-testid="user-email"
                  />
                </div>
                
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Password</label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                    required
                    minLength={8}
                    data-testid="user-password"
                  />
                  <p className="text-slate-500 text-xs mt-1">Minimum 8 characters</p>
                </div>
                
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Role</label>
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                    data-testid="user-role"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="client">Client</option>
                  </select>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateUserModal(false);
                      setUserForm({ email: '', firstName: '', lastName: '', password: '', role: 'user' });
                      setActionError('');
                    }}
                    className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-500 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="create-user-submit"
                  >
                    {actionLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4" />
                        Create User
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {showResetPasswordModal && resetPasswordUser && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-md border border-slate-700 shadow-xl">
              <div className="p-6 border-b border-slate-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Reset Password</h3>
                  <button
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setResetPasswordUser(null);
                      setNewPassword('');
                      setActionError('');
                    }}
                    className="p-1 text-slate-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-slate-400 text-sm mt-1">
                  Set a new password for <span className="text-violet-400">{resetPasswordUser.email}</span>
                </p>
              </div>
              
              <form onSubmit={handleResetPassword} className="p-6 space-y-4">
                {actionError && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {actionError}
                  </div>
                )}
                
                <div className="p-4 bg-slate-700/50 rounded-lg">
                  <div className="text-slate-300 font-medium">
                    {resetPasswordUser.firstName} {resetPasswordUser.lastName}
                  </div>
                  <div className="text-slate-500 text-sm">{resetPasswordUser.email}</div>
                </div>
                
                <div>
                  <label className="block text-slate-300 text-sm mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                    required
                    minLength={8}
                    placeholder="Enter new password"
                    data-testid="new-password-input"
                  />
                  <p className="text-slate-500 text-xs mt-1">Minimum 8 characters</p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setResetPasswordUser(null);
                      setNewPassword('');
                      setActionError('');
                    }}
                    className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading || newPassword.length < 8}
                    className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-500 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="reset-password-submit"
                  >
                    {actionLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      <>
                        <UserCog className="w-4 h-4" />
                        Reset Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
